// Account — sign-in + signed-in shell (PRD 012 increment 1). The wallet, credit
// packs, and connector install land in later increments; this is the durable
// account surface they hang off. Auth state comes from the hosted better-auth
// Worker via the hand-rolled client (src/lib/auth/authClient.ts).

import { Loader2, LogOut, Mail } from 'lucide-react'
import { useState } from 'react'
import { ReadingColumn } from '@/components/shell/AppShell'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  signInMagicLink,
  signInSocial,
  signOut,
  useAuth,
  type AuthUser
} from '@/lib/auth/authClient'

export function Account() {
  const { status, user } = useAuth()

  if (status === 'loading') {
    return (
      <ReadingColumn>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          <span className="text-body-sm">Loading your account…</span>
        </div>
      </ReadingColumn>
    )
  }

  return (
    <ReadingColumn>
      {user ? <SignedIn user={user} /> : <SignIn />}
    </ReadingColumn>
  )
}

// --- signed in --------------------------------------------------------------

function SignedIn({ user }: { user: AuthUser }) {
  const initials = (user.name || user.email || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="font-serif text-display-m">Account</h1>
        <p className="text-body-sm text-muted-foreground">
          Signed in to convariance.
        </p>
      </header>

      <div className="flex items-center gap-4 rounded-xl border border-border bg-surface p-5">
        <Avatar className="size-12">
          {user.image ? <AvatarImage src={user.image} alt={user.name} /> : null}
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-text">{user.name || 'You'}</div>
          <div className="truncate text-body-sm text-muted-foreground">{user.email}</div>
        </div>
        <Button variant="outline" size="sm" onClick={() => void signOut()}>
          <LogOut className="size-4" />
          Sign out
        </Button>
      </div>

      {/* Wallet, credit packs, and the connector install arrive in PRD 012
          increments 4–5 / 2. Placeholder so the surface reads intentionally. */}
      <section className="rounded-xl border border-dashed border-border p-5 text-body-sm text-muted-foreground">
        Credits &amp; the Claude connector are coming soon. For now, start a
        session and pair your own Claude Code as the AI participant.
      </section>
    </div>
  )
}

// --- signed out -------------------------------------------------------------

function SignIn() {
  const [busy, setBusy] = useState<null | 'google' | 'github' | 'email'>(null)
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function social(provider: 'google' | 'github') {
    setError(null)
    setBusy(provider)
    try {
      await signInSocial(provider) // navigates away on success
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed')
      setBusy(null)
    }
  }

  async function magicLink(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setError(null)
    setBusy('email')
    try {
      await signInMagicLink(email.trim())
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the link')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mx-auto max-w-sm space-y-6">
      <header className="space-y-1 text-center">
        <h1 className="font-serif text-display-m">Sign in</h1>
        <p className="text-body-sm text-muted-foreground">
          Sign in to convariance to save sessions and manage credits.
        </p>
      </header>

      <div className="space-y-2">
        <Button
          variant="outline"
          className="w-full justify-center gap-3"
          disabled={busy !== null}
          onClick={() => void social('google')}
        >
          {busy === 'google' ? <Loader2 className="size-4 animate-spin" /> : <GoogleIcon />}
          Continue with Google
        </Button>
        <Button
          variant="outline"
          className="w-full justify-center gap-3"
          disabled={busy !== null}
          onClick={() => void social('github')}
        >
          {busy === 'github' ? <Loader2 className="size-4 animate-spin" /> : <GitHubIcon />}
          Continue with GitHub
        </Button>
      </div>

      <div className="flex items-center gap-3 text-body-sm text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        or
        <span className="h-px flex-1 bg-border" />
      </div>

      {sent ? (
        <div className="rounded-lg border border-border bg-surface p-4 text-center text-body-sm">
          <Mail className="mx-auto mb-2 size-5 text-accent" />
          Check <span className="font-medium text-text">{email}</span> for a sign-in
          link.
        </div>
      ) : (
        <form onSubmit={magicLink} className="space-y-2">
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy !== null}
          />
          <Button
            type="submit"
            className="w-full justify-center gap-2"
            disabled={busy !== null || !email.trim()}
          >
            {busy === 'email' ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Mail className="size-4" />
            )}
            Email me a link
          </Button>
        </form>
      )}

      {error ? <p className="text-center text-body-sm text-danger">{error}</p> : null}
    </div>
  )
}

// --- brand marks (inline SVG — CSP-safe, no asset fetch) ---------------------

function GoogleIcon() {
  return (
    <svg className="size-4" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.85 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.67-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.67 2.84C6.71 7.3 9.14 5.38 12 5.38Z"
      />
    </svg>
  )
}

function GitHubIcon() {
  return (
    <svg className="size-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 1A11 11 0 0 0 8.52 22.44c.55.1.75-.24.75-.53v-1.86c-3.06.67-3.71-1.47-3.71-1.47-.5-1.27-1.22-1.61-1.22-1.61-1-.68.08-.67.08-.67 1.1.08 1.68 1.13 1.68 1.13.98 1.68 2.57 1.2 3.2.92.1-.71.38-1.2.69-1.47-2.44-.28-5.01-1.22-5.01-5.43 0-1.2.43-2.18 1.13-2.95-.11-.28-.49-1.4.11-2.91 0 0 .92-.3 3.02 1.13a10.5 10.5 0 0 1 5.5 0c2.1-1.43 3.02-1.13 3.02-1.13.6 1.51.22 2.63.11 2.91.7.77 1.13 1.75 1.13 2.95 0 4.22-2.58 5.15-5.03 5.42.4.34.75 1.01.75 2.04v3.02c0 .29.2.64.76.53A11 11 0 0 0 12 1Z" />
    </svg>
  )
}
